import { useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  deleted_at: string | null;
  mentioned_user_ids?: string[] | null;
  sender?: { full_name: string; avatar_url: string | null } | null;
}

const PAGE_SIZE = 50;

interface RawMessageRow {
  id: string;
  room_id: string;
  sender_id: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  deleted_at: string | null;
  mentioned_user_ids: string[] | null;
  users: { full_name: string; avatar_url: string | null } | null;
}

interface Page {
  messages: ChatMessage[];
  nextCursor: string | null;
}

/**
 * Paginated chat messages for a room. Newest page loads first.
 * Use `fetchNextPage` to load older messages (cursor = oldest created_at in current pages).
 */
export function useChatMessages(roomId: string | null | undefined) {
  const query = useInfiniteQuery<Page>({
    queryKey: ["chat-messages", roomId],
    enabled: !!roomId,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      let q = supabase
        .from("chat_messages")
        // `*` i stedet for navngitte kolonner: mentioned_user_ids (VIB-3025) kan
        // mangle et øyeblikk hvis frontenden deployes før migrasjonen.
        .select(`
          *,
          users!chat_messages_sender_id_fkey ( full_name, avatar_url )
        `)
        .eq("room_id", roomId!)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(PAGE_SIZE);
      if (pageParam) q = q.lt("created_at", pageParam as string);

      const { data, error } = await q;
      if (error) throw error;

      const rows = (data ?? []) as unknown as RawMessageRow[];
      const messages: ChatMessage[] = rows.map((m) => ({
        ...m,
        sender: m.users ?? null,
      }));
      const nextCursor = rows.length === PAGE_SIZE ? rows[rows.length - 1].created_at : null;
      return { messages, nextCursor };
    },
    getNextPageParam: (last) => last.nextCursor,
  });

  // Realtime: sentralisert via useRealtimeListeners (REALTIME_TABLES).
  // CACHE_REGISTRY mapper chat_messages → ["chat-messages", roomId] og fan-outer
  // automatisk når en INSERT-event kommer fra Supabase.

  // Returner meldingene i kronologisk rekkefølge (eldst → nyest)
  const flat: ChatMessage[] = (query.data?.pages ?? [])
    .flatMap((p) => p.messages)
    .slice()
    .reverse();

  return {
    messages: flat,
    isLoading: query.isLoading,
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => query.fetchNextPage(),
  };
}
